import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
} from "@mui/material";
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { isDateTimeGreaterThan30MinutesAgo } from '../../../../utils/dateTimeUtils';
import DeleteConfirmationModal from '../../../Modals/delete-modal';
import DynamicTable from '../../../Dynamic-table';
import axios from 'axios';
import { toast } from 'react-toastify';

interface Vital {
    temperature: string;
    rr: string;
    hr: string;
    bp: string;
    weight: string;
    status: string;
    comment: string;
    date_time: string;
    doctor: string;
    complaint: string;
    detail: string;
}

interface FormData {
    vitals: Vital;
}

interface DailyProgressNotesProps {
    onChange: (data: any) => void;
    data: FormData;
    otherTabsData: any;
    profile: any;
    handleSave: (shouldClose: boolean, searchId?: any) => void;
}

const DailyProgressNotes: React.FC<DailyProgressNotesProps> = ({ onChange, data, otherTabsData, profile, handleSave }) => {
    const [formData, setFormData] = useState<FormData>({
        vitals: {
            doctor: profile?.data?.fullName || '',
            date_time: dayjs().format('DD.MM.YYYY hh:mm A'),
            temperature: '',
            rr: '',
            hr: '',
            bp: '',
            weight: '',
            status: '',
            comment: '',
            complaint: '',
            detail: '',
        },
    });
    const [savedVitals, setSavedVitals] = useState<any[]>([]);
    const [selectedVitalIndex, setSelectedVitalIndex] = useState<number | null>(null);
    const [isMultiline, setIsMultiline] = useState(false);
    const [isComponentVisible, setIsComponentVisible] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [clickSave, setClickSave] = useState<any>(false);
    const [searchId, setSearchId] = useState<any>();

    const fetchVitals = async (admissionId: string) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_IPD_API_URL}/dpn/getDpnByAdmissionId/${admissionId}`,
                {
                    headers: {
                        'Accept': '*/*',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            const fetchedVitals = response?.data.map((vital: any) => ({
                ...vital,
                date_time: dayjs(vital?.date_time, 'DD-MM-YYYYTHH:mm').format('DD.MM.YYYY hh:mm A'),
            }));
            setSavedVitals(fetchedVitals);
            onChange({ vitals: fetchedVitals });
        } catch (error) {
            console.error('Error fetching vitals:', error);
        }
    };

    useEffect(() => {
        if (otherTabsData?._id) {
            setSearchId(otherTabsData._id);
            fetchVitals(otherTabsData._id);
        }
    }, [otherTabsData?._id]);

    useEffect(() => {
        if (data && Array.isArray(data?.vitals)) {
            setSavedVitals(data?.vitals);
        } else {
            setSavedVitals([]);
        }
    }, [data]);

    const handleDeleteClick = (index: number) => {
        setSelectedVitalIndex(index);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (selectedVitalIndex !== null) {
            deleteVital(selectedVitalIndex);
        }
        setIsDeleteModalOpen(false);
        setSelectedVitalIndex(null);
    };

    const handleDeleteModalClose = () => {
        setIsDeleteModalOpen(false);
        setSelectedVitalIndex(null);
    };

    const handleChange21 = (field: keyof Vital, value: string) => {
        setFormData(prevData => ({
            ...prevData,
            vitals: { ...prevData.vitals, [field]: value },
        }));
    };

    const handleFocus = () => {
        setIsMultiline(true);
    };

    const handleBlur = () => {
        setIsMultiline(false);
    };

    const handleChange = (field: keyof Vital, value: string) => {
        const updatedVitals = { ...formData.vitals, [field]: value };
        setFormData(prevState => ({ ...prevState, vitals: updatedVitals }));
        onChange({ ...formData, vitals: updatedVitals });
    };

    const saveData = async () => {
        try {
            // Format the date_time to match the required format (DD.MM.YYYY hh:mm A)
            const formattedDateTime = dayjs(formData.vitals.date_time, 'DD.MM.YYYY hh:mm A').format('DD.MM.YYYY hh:mm A');

            // Prepare the payload for the API
            const payload = {
                admissionId: otherTabsData?._id || '',
                temperature: formData.vitals.temperature || '',
                rr: formData?.vitals?.rr || '',
                hr: formData?.vitals?.hr || '',
                bp: formData?.vitals?.bp || '',
                weight: formData?.vitals?.weight || '',
                status: formData?.vitals?.status || '',
                comment: formData?.vitals?.comment || '',
                date_time: formattedDateTime,
                doctor: formData?.vitals?.doctor || '',
                complaint: formData?.vitals?.complaint || '',
                detail: formData?.vitals?.detail || '',
            };

            let response;
            if (selectedVitalIndex !== null) {
                // Update existing vital
                response = await axios.put(
                    `${process.env.REACT_APP_IPD_API_URL}/dpn/updateDpnByIndex/${selectedVitalIndex}`,
                    payload,
                    {
                        headers: {
                            'Accept': '*/*',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                
                if(response?.data?.isSuccess){
                    toast.success('Vitals updated Successfully');
                }


            } else {
                // Create new vital
                response = await axios.post(
                    `${process.env.REACT_APP_IPD_API_URL}/dpn/create-dpn`,
                    payload,
                    {
                        headers: {
                            'Accept': '*/*',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                 
                if(response?.data?.isSuccess){
                    toast.success('Vitals added Successfully');
                }
            }

            // Update local state after successful API call
            if (selectedVitalIndex !== null) {
                const updatedVitals = [...savedVitals];
                updatedVitals[selectedVitalIndex] = { ...formData?.vitals, date_time: formattedDateTime };
                setSavedVitals(updatedVitals);
                onChange({ vitals: updatedVitals });
                setSelectedVitalIndex(null);
            } else {
                setSavedVitals((prev: any[]) => {
                    const updatedVitals = [ { ...formData?.vitals, date_time: formattedDateTime }, ...prev,];
                    onChange({ vitals: updatedVitals });
                    return updatedVitals;
                });
            }

            // Reset form
            setFormData({
                vitals: {
                    temperature: '',
                    rr: '',
                    hr: '',
                    bp: '',
                    weight: '',
                    status: '',
                    comment: '',
                    date_time: dayjs().format('DD.MM.YYYY hh:mm A'),
                    doctor: profile?.data?.fullName || '',
                    complaint: '',
                    detail: '',
                },
            });

            setClickSave(true);
            setTimeout(() => setClickSave(false), 1000);
        } catch (error) {
            console.error('Error processing data:', error);
            alert('Failed to save data. Please try again.');
        }
    };

    const deleteVital = async (index: number) => {
        try {
            const admissionId = otherTabsData?._id || '';
            let response:any=   await axios.delete(
                `${process.env.REACT_APP_IPD_API_URL}/dpn/deleteDpnByIndex/${admissionId}/${index}`,
                {
                    headers: {
                        'Accept': '*/*',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

             
            if(response?.data?.isSuccess){
                toast.success('Vitals deleted Successfully');
            }

            
            const updatedVitals = savedVitals?.filter((_: any, i: any) => i !== index);
            setSavedVitals(updatedVitals);
            onChange({ vitals: updatedVitals });
        } catch (error) {
            console.error('Error deleting vital:', error);
            alert('Failed to delete vital. Please try again.');
        }
    };

    const editVital = (index: number) => {
        const selectedVital = savedVitals[index];
        setFormData({ vitals: selectedVital });
        setSelectedVitalIndex(index);
        setIsComponentVisible(true);
    };

    return (
        <>
            <Box
                sx={{
                    bgcolor: "rgba(233, 233, 237, 1)",
                    padding: 0.25,
                    textAlign: "center",
                    mt: 0.5,
                    mb: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '30px',
                }}
            >
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                    Vitals and Complaints
                </Typography>
                {/* <FormControlLabel
                    control={
                        <Switch
                            checked={isComponentVisible}
                            onChange={() => setIsComponentVisible(!isComponentVisible)}
                            color="primary"
                        />
                    }
                    label="+Add Vital"
                /> */}
            </Box>

                <Box mt={1} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Grid container spacing={1.4}>
                        <Grid item md={1.4} sm={2} xs={3}>
                            <Box display="flex" alignItems="center" className="search-field">
                                <TextField
                                    label="Temp"
                                    placeholder="92-107"
                                    size="small"
                                    value={formData?.vitals?.temperature}
                                    onChange={(e) => {
                                        const value: any = Number(e.target.value);
                                        if (!isNaN(value) && value <= 107) {
                                            handleChange('temperature', value.toString());
                                        }
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontSize: '10px',
                                            borderRadius: '8px',
                                            backgroundColor: "#F5F5FB",
                                            height: '30px',
                                        },
                                    }}
                                    error={
                                        !!formData?.vitals?.temperature &&
                                        (parseFloat(formData?.vitals?.temperature) < 92 || parseFloat(formData.vitals.temperature) > 107)
                                    }
                                />
                                <Typography sx={{ padding: "10px", ml: 1, fontWeight: "500", fontSize: '12px' }}>
                                    F
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item md={1.4} sm={2} xs={3}>
                            <Box display="flex" alignItems="center" className="search-field">
                                <TextField
                                    label="RR"
                                    placeholder="1-80"
                                    size="small"
                                    value={formData?.vitals?.rr}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value) && (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 80))) {
                                            handleChange('rr', value);
                                        }
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontSize: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: "#F5F5FB",
                                        },
                                    }}
                                    error={!!formData?.vitals?.rr && (parseInt(formData?.vitals?.rr) < 1 || parseInt(formData.vitals.rr) > 80)}
                                    helperText={
                                        formData?.vitals?.rr &&
                                            (parseInt(formData?.vitals?.rr) < 1 || parseInt(formData?.vitals?.rr) > 80)
                                            ? 'Please enter a value between 1 and 80'
                                            : ''
                                    }
                                />
                                <Typography sx={{ padding: "10px", ml: 1, fontWeight: "500", fontSize: '12px' }}>
                                    /Min
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item md={1.4} sm={2} xs={3}>
                            <Box display="flex" alignItems="center" className="search-field">
                                <TextField
                                    label="HR"
                                    placeholder="50-250"
                                    size="small"
                                    value={formData?.vitals?.hr}
                                    onChange={(e) => {
                                        const value: any = e.target.value;
                                        if (!isNaN(value) && value <= 250) {
                                            handleChange('hr', value.toString());
                                        }
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontSize: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: "#F5F5FB",
                                        },
                                    }}
                                />
                                <Typography sx={{ padding: "10px", ml: 1, fontWeight: "500", fontSize: '12px' }}>
                                    /Min
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item md={1.6} sm={2} xs={3}>
                            <Box display="flex" alignItems="center" className="search-field">
                                <TextField
                                    label="BP"
                                    placeholder="120/80"
                                    size="small"
                                    value={formData?.vitals?.bp}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value) {
                                            handleChange('bp', '');
                                            return;
                                        }
                                        const regex = /^(\d{1,3})\/?(\d{0,3})$/;
                                        if (regex.test(value)) {
                                            const parts = value.split('/');
                                            const firstPart = parseInt(parts[0], 10);
                                            const secondPart = parts[1] ? parseInt(parts[1], 10) : 0;
                                            if (firstPart >= 1 && firstPart <= 300 && secondPart <= firstPart) {
                                                handleChange('bp', value);
                                            }
                                        }
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontSize: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: "#F5F5FB",
                                        },
                                    }}
                                />
                                <Typography sx={{ padding: "5px", fontWeight: "500", fontSize: '12px' }}>
                                    mmHg
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item md={1.4} sm={2} xs={3}>
                            <Box display="flex" alignItems="center" className="search-field">
                                <TextField
                                    label="SPO₂"
                                    placeholder="90-100"
                                    size="small"
                                    value={formData?.vitals?.weight}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d{0,3}$/.test(value)) {
                                            const numericValue = parseInt(value, 10);
                                            if (value === '' || (numericValue >= 1 && numericValue <= 100)) {
                                                handleChange('weight', value);
                                            }
                                        }
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontSize: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: "#F5F5FB",
                                        },
                                    }}
                                />
                                <Typography sx={{ padding: "10px", ml: 1, fontWeight: "500", fontSize: '12px' }}>
                                    %
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item md={1.4} sm={6} xs={12}>
                            <TextField
                                select
                                label="Status"
                                size="small"
                                value={formData?.vitals?.status}
                                onChange={(e) => handleChange('status', e.target.value)}
                                fullWidth
                                className='search-field'
                            >
                                <MenuItem value="">Select</MenuItem>
                                {['Improving', 'Not Improving', 'Deteriorated', 'Sick Critical', "Static"]?.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item md={3} sm={6} xs={12}>
                            <TextField
                                label="Complaint"
                                size="small"
                                value={formData?.vitals?.complaint}
                                onChange={(e) => handleChange('complaint', e.target.value)}
                                className='search-field'
                                fullWidth
                            />
                        </Grid>

                        <Grid item md={2} sm={6} xs={12}>
                            <TextField
                                label="Detail"
                                size="small"
                                value={formData?.vitals?.detail}
                                onChange={(e) => handleChange('detail', e.target.value)}
                                className='search-field'
                                fullWidth
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <TextField
                                label="Comment"
                                placeholder="Comment"
                                value={formData?.vitals?.comment}
                                onChange={(e) => handleChange21('comment', e.target.value)}
                                fullWidth
                                size="small"
                                InputProps={{
                                    style: {
                                        borderRadius: "12px",
                                        background: "#F5F5FB",
                                        fontSize: "12px",
                                    },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            border: "none",
                                        },
                                        "&:hover fieldset": {
                                            border: "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "none",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        top: "-2px",
                                    },
                                }}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                multiline={isMultiline}
                                minRows={isMultiline ? 3 : 1}
                            />
                        </Grid>
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mt: 1.5 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={saveData}
                                size="small"
                                disabled={
                                    !formData?.vitals?.temperature &&
                                    !formData?.vitals?.rr &&
                                    !formData?.vitals?.hr &&
                                    !formData?.vitals?.bp &&
                                    !formData?.vitals?.weight &&
                                    !formData?.vitals?.status &&
                                    !formData?.vitals?.comment &&
                                    !formData?.vitals?.complaint &&
                                    !formData?.vitals?.detail
                                }
                                sx={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    minWidth: '50px',
                                    height: '30px',
                                }}
                            >
                                {selectedVitalIndex !== null ? 'Update' : 'Save'}
                            </Button>
                            {!(
                                !formData?.vitals?.temperature &&
                                !formData?.vitals?.rr &&
                                !formData?.vitals?.hr &&
                                !formData?.vitals?.bp &&
                                !formData?.vitals?.weight &&
                                !formData?.vitals?.status &&
                                !formData?.vitals?.comment &&
                                !formData?.vitals?.complaint &&
                                !formData?.vitals?.detail
                            ) && (
                                    <Typography sx={{ ml: 1, fontSize: '12px', color: '#FF0000' }}>
                                        {selectedVitalIndex !== null ? 'Must click the UPDATE button to save' : 'Must click the SAVE button to save'}
                                    </Typography>
                                )}                        </Box>
                        <Divider sx={{ width: '100%', mt: 1, mb: 1 }} />
                    </Grid>
                </Box>
          
            <DynamicTable
                headers={[
                    "SN",
                    "Date",
                    "Temp",
                    "RR",
                    "HR",
                    "BP",
                    "SpO2",
                    "Status",
                    "Comment",
                    "Complaint",
                    "Detail",
                    "Created By",
                    "Action",
                ]}
                data={savedVitals?.map((vital: any, index: any) => [
                    index + 1,
                    vital?.date_time,
                    vital?.temperature,
                    vital?.rr,
                    vital?.hr,
                    vital?.bp,
                    vital?.weight,
                    vital?.status,
                    vital?.comment,
                    vital?.complaint,
                    vital?.detail,
                    vital?.doctor,
                ])}
                actionButtons={[
                    {
                        icon: (rowIndex: number) => {
                            const isDisabled = !isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time);
                            return (
                                <EditIcon
                                    sx={{
                                        color: isDisabled ? "#D3D3D3" : "#4CAF50",
                                        minWidth: "20.09px",
                                        minHeight: "20.09px",
                                        height: 20.09,
                                        width: 29.09,
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        opacity: isDisabled ? 0.5 : 1,
                                        "&:hover": {
                                            background: "none",
                                            color: isDisabled ? "#D3D3D3" : "rgb(72, 141, 72)",
                                        },
                                    }}
                                />
                            );
                        },
                        onClick: (rowIndex: number) => {
                            const isDisabled = !isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time);
                            if (!isDisabled) {
                                editVital(rowIndex);
                            }
                        },
                        disabled: (rowIndex: number) => !isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time),
                    },
                    {
                        icon: (rowIndex: number) => (
                            <DeleteIcon
                                sx={{
                                    color: isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time) ? "#FF5454" : "#D3D3D3",
                                    minWidth: "20.09px",
                                    minHeight: "20.09px",
                                    height: 20.09,
                                    width: 29.09,
                                    cursor: isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time) ? "pointer" : "not-allowed",
                                    "&:hover": {
                                        background: "none",
                                        color: isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time) ? "rgb(191, 44, 44)" : "#D3D3D3",
                                    },
                                }}
                            />
                        ),
                        onClick: (rowIndex: number) => handleDeleteClick(rowIndex),
                        disabled: (rowIndex: number) => !isDateTimeGreaterThan30MinutesAgo(savedVitals[rowIndex]?.date_time),
                    },
                ]}
            />
            <DeleteConfirmationModal
                open={isDeleteModalOpen}
                handleClose={handleDeleteModalClose}
                handleConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default DailyProgressNotes;